import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../context/AuthContext';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    Legend,
} from 'recharts';
import {
    TrendingUp,
    Star,
    Flame,
    BookOpenCheck,
    Trophy,
    Sparkles,
    AlertTriangle,
    Target,
    ArrowRight,
    Plus,
    Trash2,
    CheckCircle2,
} from 'lucide-react';

const SUBJECTS = ['Business', 'Economics', 'Geography'];

// -------------------- Small presentational helpers --------------------

function StatCard({ icon: Icon, label, value, hint, testId, tone = 'default' }) {
    const tones = {
        default: 'bg-white',
        accent: 'bg-gradient-to-br from-[#F4ECFF] to-white',
    };
    return (
        <div
            className={`${tones[tone]} rounded-2xl border border-zinc-100 p-5 shadow-[0_2px_10px_-3px_rgba(124,58,237,0.08)] hover:shadow-[0_8px_20px_-6px_rgba(124,58,237,0.18)] transition-all`}
            data-testid={testId}
        >
            <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[#F4ECFF] flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#7C3AED]" />
                </div>
                <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">{label}</span>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-zinc-950 leading-tight">{value}</div>
            {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
        </div>
    );
}

function SkeletonBlock({ className = '' }) {
    return <div className={`animate-pulse bg-zinc-100 rounded-lg ${className}`} />;
}

function SectionHeader({ title, subtitle, right }) {
    return (
        <div className="flex items-start justify-between mb-4 gap-3">
            <div>
                <h2 className="text-lg font-black text-zinc-950">{title}</h2>
                {subtitle && <p className="text-sm text-zinc-500 mt-0.5">{subtitle}</p>}
            </div>
            {right}
        </div>
    );
}

function EmptyState({ title, subtitle }) {
    return (
        <div className="bg-white rounded-2xl border border-zinc-100 p-8 text-center" data-testid="performance-empty-state">
            <div className="w-12 h-12 rounded-full bg-[#F4ECFF] mx-auto flex items-center justify-center mb-3">
                <Sparkles className="w-6 h-6 text-[#7C3AED]" />
            </div>
            <h3 className="text-base font-bold text-zinc-950 mb-1">{title}</h3>
            <p className="text-sm text-zinc-500 max-w-md mx-auto">{subtitle}</p>
        </div>
    );
}

// -------------------- Progress chart --------------------

function ProgressChart({ data, range, setRange, loading }) {
    const ranges = [
        { id: '7d', label: '7 days' },
        { id: '30d', label: '30 days' },
        { id: '3m', label: '3 months' },
        { id: 'all', label: 'All time' },
    ];

    return (
        <div
            className="bg-white rounded-2xl border border-zinc-100 p-5 shadow-[0_2px_10px_-3px_rgba(124,58,237,0.08)]"
            data-testid="progress-chart-card"
        >
            <SectionHeader
                title="Progress over time"
                subtitle="Your average score across all marked questions"
                right={
                    <div className="flex gap-1.5">
                        {ranges.map((r) => (
                            <button
                                key={r.id}
                                onClick={() => setRange(r.id)}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                                    range === r.id
                                        ? 'bg-[#7C3AED] text-white'
                                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                                }`}
                                data-testid={`progress-range-${r.id}`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                }
            />
            <div className="h-64">
                {loading ? (
                    <SkeletonBlock className="w-full h-full" />
                ) : data.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-sm text-zinc-400">
                        No data in this range yet.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: -20 }}>
                            <defs>
                                <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.35} />
                                    <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" />
                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#71717A' }} tickLine={false} axisLine={false} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#71717A' }} tickLine={false} axisLine={false} />
                            <Tooltip
                                contentStyle={{
                                    borderRadius: 12,
                                    border: '1px solid #E4E4E7',
                                    fontSize: 12,
                                }}
                                formatter={(v) => [`${v}%`, 'Average']}
                            />
                            <Line
                                type="monotone"
                                dataKey="score"
                                stroke="#7C3AED"
                                strokeWidth={2.5}
                                dot={{ r: 4, fill: '#7C3AED', strokeWidth: 0 }}
                                activeDot={{ r: 6, fill: '#7C3AED' }}
                                fill="url(#scoreGrad)"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}

// -------------------- Subject analytics --------------------

function SubjectAnalytics({ subject }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get(`/api/performance/subject/${subject}`)
            .then((res) => {
                if (mounted) setData(res.data);
            })
            .catch(() => {
                if (mounted) setData(null);
            })
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, [subject]);

    if (loading) {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[0, 1, 2, 3].map((i) => (
                    <SkeletonBlock key={i} className="h-56" />
                ))}
            </div>
        );
    }

    if (!data || !data.has_data) {
        return (
            <EmptyState
                title={`No ${subject} data yet`}
                subtitle={`Mark a few ${subject} questions to unlock detailed insights for this subject.`}
            />
        );
    }

    return (
        <div className="space-y-4 fade-in">
            {/* AI Insight */}
            <div
                className="bg-gradient-to-br from-[#7C3AED] to-[#6D28D9] rounded-2xl p-5 text-white shadow-[0_10px_30px_-10px_rgba(124,58,237,0.4)]"
                data-testid={`${subject.toLowerCase()}-insight`}
            >
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider mb-1 opacity-80">AI Insight</div>
                        <p className="text-sm sm:text-base leading-relaxed">{data.insight}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Strong Topics */}
                <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid={`${subject.toLowerCase()}-strong-topics`}>
                    <SectionHeader
                        title="Strong topics"
                        subtitle={data.strong_topics.length === 0 ? 'Keep practising to unlock your strengths.' : undefined}
                    />
                    <ul className="space-y-2">
                        {data.strong_topics.map((t) => (
                            <li key={t.topic} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50/60 border border-emerald-100">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    <span className="text-sm font-bold text-zinc-950">{t.topic}</span>
                                </div>
                                <span className="text-xs font-bold text-emerald-700">{t.average}%</span>
                            </li>
                        ))}
                        {data.strong_topics.length === 0 && <li className="text-sm text-zinc-400">—</li>}
                    </ul>
                </div>

                {/* Weak Topics */}
                <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid={`${subject.toLowerCase()}-weak-topics`}>
                    <SectionHeader
                        title="Weak topics"
                        subtitle={data.weak_topics.length === 0 ? 'No weak topics detected.' : undefined}
                    />
                    <ul className="space-y-2">
                        {data.weak_topics.map((t) => (
                            <li key={t.topic} className="flex items-center justify-between p-3 rounded-lg bg-rose-50/60 border border-rose-100">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-rose-500" />
                                    <span className="text-sm font-bold text-zinc-950">{t.topic}</span>
                                </div>
                                <span className="text-xs font-bold text-rose-600">{t.average}%</span>
                            </li>
                        ))}
                        {data.weak_topics.length === 0 && <li className="text-sm text-zinc-400">—</li>}
                    </ul>
                </div>

                {/* Topic Radar */}
                <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid={`${subject.toLowerCase()}-radar`}>
                    <SectionHeader title="Topic radar" subtitle="Average score by topic" />
                    <div className="h-64">
                        {data.topic_radar.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-sm text-zinc-400">
                                Not enough data yet.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <RadarChart data={data.topic_radar} outerRadius={95}>
                                    <PolarGrid stroke="#E4E4E7" />
                                    <PolarAngleAxis dataKey="topic" tick={{ fontSize: 10, fill: '#52525B' }} />
                                    <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#A1A1AA' }} />
                                    <Radar name="Score" dataKey="score" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.35} />
                                </RadarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Revision Priority */}
                <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid={`${subject.toLowerCase()}-priority`}>
                    <SectionHeader title="Revision priority" subtitle="Weakest topics first" />
                    <ol className="space-y-2.5">
                        {data.revision_priority.map((p) => (
                            <li key={p.topic} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-50">
                                <div className="w-7 h-7 rounded-lg bg-[#7C3AED] text-white text-xs font-black flex items-center justify-center flex-shrink-0">
                                    {p.rank}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-zinc-950">{p.topic}</div>
                                    <div className="text-xs text-zinc-500">
                                        avg {p.average}% • {p.count} attempt{p.count === 1 ? '' : 's'}
                                    </div>
                                </div>
                            </li>
                        ))}
                        {data.revision_priority.length === 0 && <li className="text-sm text-zinc-400">—</li>}
                    </ol>
                </div>

                {/* Common Mistakes */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-zinc-100 p-5" data-testid={`${subject.toLowerCase()}-mistakes`}>
                    <SectionHeader title="Common mistakes" subtitle="Patterns detected across your answers" />
                    {data.common_mistakes.length === 0 ? (
                        <p className="text-sm text-zinc-400">None detected.</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {data.common_mistakes.map((m) => (
                                <span
                                    key={m.label}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F4ECFF] text-[#7C3AED] text-xs font-bold"
                                >
                                    {m.label}
                                    <span className="bg-[#7C3AED] text-white rounded-full px-1.5 py-0.5 text-[10px]">{m.count}</span>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// -------------------- Revise Now --------------------

function ReviseNow({ onPractice }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/api/performance/revise-now')
            .then((res) => setItems(res.data.items || []))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => (
                    <SkeletonBlock key={i} className="h-36" />
                ))}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <EmptyState
                title="Nothing to revise yet"
                subtitle="Once you have marked a few questions we'll pinpoint your weakest topics here."
            />
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="revise-now-list">
            {items.map((it, idx) => (
                <div
                    key={`${it.subject}-${it.topic}`}
                    className="bg-white rounded-2xl border border-zinc-100 p-5 hover:shadow-[0_10px_30px_-10px_rgba(124,58,237,0.25)] transition-all"
                    data-testid={`revise-card-${idx}`}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F4ECFF] text-[#7C3AED] uppercase tracking-wider">
                            {it.subject}
                        </span>
                        <span className="text-[10px] font-bold text-zinc-400">avg {it.average}%</span>
                    </div>
                    <h3 className="text-lg font-black text-zinc-950 mb-1">{it.topic}</h3>
                    <p className="text-xs text-zinc-500 mb-4">{it.reason}</p>
                    <button
                        onClick={() => onPractice && onPractice(it)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#7C3AED] hover:text-[#6D28D9]"
                        data-testid={`revise-practice-${idx}`}
                    >
                        Practice now <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            ))}
        </div>
    );
}

// -------------------- Strengths --------------------

function Strengths() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/api/performance/strengths')
            .then((res) => setItems(res.data.items || []))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <SkeletonBlock className="h-24" />;
    if (items.length === 0) return null;

    return (
        <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid="strengths-card">
            <SectionHeader title="Your strongest areas" subtitle="Celebrate the wins" />
            <div className="flex flex-wrap gap-3">
                {items.map((it) => (
                    <div
                        key={`${it.subject}-${it.topic}`}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-100"
                    >
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <div>
                            <div className="text-xs font-bold text-zinc-500 leading-none">{it.subject}</div>
                            <div className="text-sm font-black text-zinc-950">{it.topic}</div>
                        </div>
                        <span className="text-xs font-bold text-amber-700 ml-2">{it.average}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// -------------------- Mistake patterns --------------------

function MistakePatterns() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/api/performance/mistake-patterns')
            .then((res) => setData(res.data))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <SkeletonBlock className="h-48" />;
    if (!data.items || data.items.length === 0) return null;

    const max = Math.max(...data.items.map((i) => i.count), 1);

    return (
        <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid="mistake-patterns-card">
            <SectionHeader title="Mistake patterns" subtitle={`Across all ${data.total} answers`} />
            <ul className="space-y-2.5">
                {data.items.map((m) => (
                    <li key={m.label} className="flex items-center gap-3">
                        <span className="w-40 flex-shrink-0 text-sm font-semibold text-zinc-700">{m.label}</span>
                        <div className="flex-1 bg-zinc-100 rounded-full h-2.5 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-[#A78BFA] to-[#7C3AED]"
                                style={{ width: `${(m.count / max) * 100}%` }}
                            />
                        </div>
                        <span className="w-10 text-right text-xs font-bold text-zinc-500">{m.count}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// -------------------- Goals --------------------

const GOAL_PRESETS = [
    { type: 'average_score', target: 80, label: 'Reach 80% average' },
    { type: 'questions_completed', target: 20, label: 'Complete 20 questions' },
    { type: 'subject_average', subject: 'Economics', target: 75, label: 'Improve Economics to 75%' },
];

function Goals() {
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPicker, setShowPicker] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get('/api/performance/goals')
            .then((res) => setGoals(res.data.items || []))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const addGoal = async (preset) => {
        await api.post('/api/performance/goals', preset);
        setShowPicker(false);
        load();
    };

    const deleteGoal = async (g) => {
        const q = g.subject ? `?subject=${g.subject}` : '';
        await api.delete(`/api/performance/goals/${g.type}${q}`);
        load();
    };

    return (
        <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid="goals-card">
            <SectionHeader
                title="Goals & targets"
                subtitle="Set targets to keep yourself accountable"
                right={
                    <button
                        onClick={() => setShowPicker((v) => !v)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#7C3AED] hover:bg-[#6D28D9] px-3 py-1.5 rounded-lg transition-colors"
                        data-testid="add-goal-button"
                    >
                        <Plus className="w-3.5 h-3.5" /> Add goal
                    </button>
                }
            />

            {showPicker && (
                <div className="mb-4 p-3 rounded-xl bg-zinc-50 border border-zinc-100">
                    <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Pick a goal</div>
                    <div className="flex flex-wrap gap-2">
                        {GOAL_PRESETS.map((p) => (
                            <button
                                key={p.label}
                                onClick={() => addGoal(p)}
                                className="text-xs font-semibold px-3 py-2 rounded-lg bg-white border border-zinc-200 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors"
                                data-testid={`goal-preset-${p.type}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <SkeletonBlock className="h-20" />
            ) : goals.length === 0 ? (
                <p className="text-sm text-zinc-500">No goals yet. Add one to start tracking.</p>
            ) : (
                <ul className="space-y-3">
                    {goals.map((g) => (
                        <li key={`${g.type}-${g.subject || ''}`} className="flex items-center gap-3">
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-bold text-zinc-950">{g.label}</span>
                                    <span className="text-xs font-bold text-zinc-500">
                                        {g.current} / {g.target}
                                        {g.type === 'questions_completed' ? '' : '%'}
                                    </span>
                                </div>
                                <div className="w-full bg-zinc-100 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-gradient-to-r from-[#A78BFA] to-[#7C3AED] transition-all"
                                        style={{ width: `${Math.min(100, g.progress)}%` }}
                                    />
                                </div>
                            </div>
                            <button
                                onClick={() => deleteGoal(g)}
                                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                                data-testid={`delete-goal-${g.type}`}
                                aria-label="Delete goal"
                            >
                                <Trash2 className="w-4 h-4 text-zinc-400" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// -------------------- Main tab --------------------

export default function PerformanceTab({ onPracticeTopic }) {
    const [overview, setOverview] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(true);

    const [range, setRange] = useState('30d');
    const [progress, setProgress] = useState([]);
    const [loadingProgress, setLoadingProgress] = useState(true);

    const [activeSubject, setActiveSubject] = useState('Business');

    useEffect(() => {
        api.get('/api/performance/overview')
            .then((res) => setOverview(res.data))
            .finally(() => setLoadingOverview(false));
    }, []);

    useEffect(() => {
        setLoadingProgress(true);
        api.get(`/api/performance/progress?range=${range}`)
            .then((res) => setProgress(res.data.overall || []))
            .finally(() => setLoadingProgress(false));
    }, [range]);

    const hasData = overview?.has_data;

    return (
        <div className="space-y-6 fade-in" data-testid="performance-tab">
            {/* Header */}
            <div>
                <h1 className="text-2xl sm:text-3xl font-black text-zinc-950">My Performance</h1>
                <p className="text-sm text-zinc-500 mt-1">
                    Track your progress, identify weak topics, and revise smarter with AI insights.
                </p>
            </div>

            {loadingOverview ? (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <SkeletonBlock key={i} className="h-28" />
                    ))}
                </div>
            ) : !hasData ? (
                <EmptyState
                    title="No data yet"
                    subtitle="Complete some marked questions to unlock performance insights, trends and personalised recommendations."
                />
            ) : (
                <>
                    {/* Overview stats */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="overview-stats">
                        <StatCard
                            icon={BookOpenCheck}
                            label="Questions"
                            value={overview.questions_completed}
                            hint="Total marked"
                            testId="stat-questions"
                            tone="accent"
                        />
                        <StatCard
                            icon={TrendingUp}
                            label="Average"
                            value={`${overview.average_score}%`}
                            hint="Across all attempts"
                            testId="stat-average"
                        />
                        <StatCard
                            icon={Trophy}
                            label="Best subject"
                            value={overview.best_subject?.name || '—'}
                            hint={overview.best_subject ? `avg ${overview.best_subject.average}%` : 'No data'}
                            testId="stat-best-subject"
                        />
                        <StatCard
                            icon={Sparkles}
                            label="Most improved"
                            value={
                                overview.most_improved_topic
                                    ? overview.most_improved_topic.topic
                                    : '—'
                            }
                            hint={
                                overview.most_improved_topic
                                    ? `+${overview.most_improved_topic.delta}% improvement`
                                    : 'Keep practising'
                            }
                            testId="stat-most-improved"
                        />
                        <StatCard
                            icon={Flame}
                            label="Streak"
                            value={`${overview.current_streak} day${overview.current_streak === 1 ? '' : 's'}`}
                            hint="Keep going!"
                            testId="stat-streak"
                        />
                    </div>

                    {/* Progress chart */}
                    <ProgressChart data={progress} range={range} setRange={setRange} loading={loadingProgress} />

                    {/* Revise now */}
                    <div>
                        <SectionHeader
                            title="What you should revise next"
                            subtitle="Your top 3 weakest topics ranked by the AI"
                        />
                        <ReviseNow onPractice={onPracticeTopic} />
                    </div>

                    {/* Strengths */}
                    <Strengths />

                    {/* Mistake patterns */}
                    <MistakePatterns />

                    {/* Goals */}
                    <Goals />

                    {/* Subject sub-tabs */}
                    <div className="bg-white rounded-2xl border border-zinc-100 p-5">
                        <SectionHeader title="Subject breakdown" subtitle="Deep-dive analytics per subject" />
                        <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="subject-subtabs">
                            {SUBJECTS.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setActiveSubject(s)}
                                    className={`px-4 py-2 text-sm font-bold rounded-lg whitespace-nowrap transition-all ${
                                        activeSubject === s
                                            ? 'bg-[#7C3AED] text-white shadow-[0_4px_14px_-4px_rgba(124,58,237,0.5)]'
                                            : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                                    }`}
                                    data-testid={`subject-subtab-${s.toLowerCase()}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <SubjectAnalytics subject={activeSubject} />
                    </div>
                </>
            )}
        </div>
    );
}
